import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * The live quantity stepper rendered inside a product card's quick-add area, goodeggs-style.
 *
 * Once a shopper taps the card's "+" button and the item lands in the cart, `quick-add.js`'s
 * sibling `<quick-add-component>` gets `data-in-cart="true"` and this stepper swaps into view.
 * From there, tapping +/- updates the cart line directly via the Cart AJAX API, no page or
 * cart-drawer visit required. Decreasing past 1 removes the line and the "+" button reappears.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {QuantitySelectorComponent}
 */
export class QuickAddQuantityComponent extends QuantitySelectorComponent {
  /** @type {((event: Event) => void) & { cancel(): void }} */
  #debouncedChange = debounce((event) => this.#handleQuantityChange(/** @type {QuantitySelectorUpdateEvent} */ (event)), 300);
  #cartUpdateAbortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedChange);

    const quickAdd = this.closest('quick-add-component');
    quickAdd?.addEventListener(StandardEvents.cartLinesUpdate, this.#handleAddToCart, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedChange);
    this.#debouncedChange.cancel();
    this.#cartUpdateAbortController.abort();
  }

  /**
   * The variant this stepper controls. Read live from the quick-add form's hidden input rather
   * than a static attribute so a swatch change on the card (which updates that input elsewhere)
   * is picked up automatically.
   * @returns {string}
   */
  get #variantId() {
    const input = this.closest('quick-add-component')?.querySelector('product-form-component [ref="variantId"]');
    return (input instanceof HTMLInputElement && input.value) || this.dataset.variantId || '';
  }

  /**
   * Cart semantics: "what should the total quantity BE", using an absolute max rather than the
   * product-page's "how many more can be added" (there is no separate quantity to add here).
   * @returns {number | null}
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max;
  }

  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const atMin = value <= 1;

    minusButton.disabled = false;
    minusButton.classList.toggle('quick-add__stepper-button--remove', atMin);

    const minusLabel = minusButton.querySelector('.visually-hidden');
    if (minusLabel) {
      minusLabel.textContent = atMin
        ? this.dataset.removeLabel || minusLabel.textContent || ''
        : this.dataset.decreaseLabel || minusLabel.textContent || '';
    }

    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Reveals the stepper (and hides the "+" button) once this card's own add-to-cart submission
   * succeeds. Scoped to this card only — `event.target` stays the originating
   * `product-form-component` even though we're listening on an ancestor.
   * @param {CartLinesUpdateEvent} event
   */
  #handleAddToCart = (event) => {
    if (event.action !== 'add' || event.context !== 'product') return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const variantId = this.#variantId;
        const items = /** @type {Array<{variant_id: number | string, quantity: number}>} */ (detail?.items || []);
        const matched = items.find((item) => String(item.variant_id) === String(variantId));
        const quantity = matched ? matched.quantity : 1;

        this.setValue(quantity.toString());
        this.refs.quantityInput.setAttribute('data-cart-quantity', quantity.toString());
        this.updateButtonStates();

        this.closest('quick-add-component')?.setAttribute('data-in-cart', 'true');
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add-quantity] Event promise rejected:', error);
      });
  };

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityChange(event) {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    this.#updateCartLine(event.detail.quantity);
  }

  /**
   * Pushes a quantity change straight to the cart via the Cart AJAX API, using the variant ID as
   * the line identifier (Shopify's `/cart/change.js` accepts either `line` or `id`).
   * @param {number} quantity
   */
  #updateCartLine(quantity) {
    const variantId = this.#variantId;
    if (!variantId) return;

    this.toggleAttribute('updating', true);

    const body = JSON.stringify({ id: variantId, quantity });
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity === 0 ? 'remove' : 'update',
        context: 'product',
        lines: [{ id: variantId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((cart) => {
        if (cart.errors) {
          throw new Error(typeof cart.errors === 'string' ? cart.errors : JSON.stringify(cart.errors));
        }

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'quick-add-quantity-component',
            didError: false,
          },
        });

        if (quantity === 0) {
          this.closest('quick-add-component')?.removeAttribute('data-in-cart');
          this.setValue('1');
          this.refs.quantityInput.setAttribute('data-cart-quantity', '0');
        }
      })
      .catch((/** @type {any} */ error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.toggleAttribute('updating', false);
        this.updateButtonStates();
      });
  }
}

if (!customElements.get('quick-add-quantity-component')) {
  customElements.define('quick-add-quantity-component', QuickAddQuantityComponent);
}
